// Shared minimap for both the TTS Timeline + STS MasterTimeline. Renders a
// 16px-tall thumbnail strip showing all chips on the timeline plus a
// draggable viewport rectangle that mirrors the main scroll wrap. Edge
// handles resize the viewport (== zoom). Body drag pans (== scroll).
//
// The parent owns the chip data and the zoom/scroll math (because both are
// derived differently per view). This component owns the minimap's own
// width measurement, the drag handlers, and the JSX. State that needs to
// flow back to the parent (scrollLeft after a drag, pxPerSec changes from
// handle resize) is exposed through callbacks.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { voiceColorVar } from '@/lib/voiceMapping';
import styles from './TimelineMinimap.module.css';

export interface MinimapChip {
  /** Start offset in seconds. */
  offset: number;
  /** Visible duration in seconds (outPoint − inPoint for STS clips, or
   *  take.duration for TTS blocks). */
  duration: number;
  /** Optional voice-id for multi-voice stacked rendering (#173). When
   *  `activeVoiceIds.length >= 2` the minimap splits into per-voice rows
   *  and uses this to pick the row + the chip's shade-color. */
  voiceId?: string;
}

interface Props {
  chips: MinimapChip[];
  totalDur: number;
  trackWidth: number;
  wrapW: number;
  scrollLeft: number;
  fitPxPerSec: number;
  pxPerSecMax: number;
  scrollWrapRef: React.RefObject<HTMLDivElement | null>;
  setScrollLeft: (n: number) => void;
  setPxPerSecManual: (n: number) => void;
  /** Gate flag the parent reads when computing the viewport rect — the
   *  parent should switch to handleDragRect-driven rendering while this
   *  is true so passive scroll updates don't fight the drag. Component
   *  flips it on pointerdown / off on pointerup. */
  handleDragActiveRef: React.MutableRefObject<boolean>;
  /** Multi-Voice (#173): active voice-ids in display order. When this has
   *  ≥2 entries, the minimap renders one stacked mini-row per voice and
   *  partitions chips by their `voiceId`. Omit/empty for single-track. */
  activeVoiceIds?: string[];
  /** Asset's full voiceIds list — needed by voiceColorVar to compute the
   *  shade for each track. Only consulted in multi-voice mode. */
  assetVoiceIds?: string[];
}

export function TimelineMinimap({
  chips,
  totalDur,
  trackWidth,
  wrapW,
  scrollLeft,
  fitPxPerSec,
  pxPerSecMax,
  scrollWrapRef,
  setScrollLeft,
  setPxPerSecManual,
  handleDragActiveRef,
  activeVoiceIds,
  assetVoiceIds,
}: Props) {
  const minimapRef = useRef<HTMLDivElement | null>(null);
  const [minimapW, setMinimapW] = useState<number>(0);
  // While a handle drag is active the viewport rect is driven by this
  // local state instead of the derived scrollLeft/trackWidth math.
  // Released on pointerup so passive scroll re-takes over.
  const [handleDragRect, setHandleDragRect] = useState<{ left: number; width: number } | null>(null);

  useEffect(() => {
    const el = minimapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setMinimapW(w);
    });
    ro.observe(el);
    setMinimapW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const minimapViewportLeft =
    trackWidth > 0 ? (scrollLeft / trackWidth) * minimapW : 0;
  const minimapViewportWidth =
    trackWidth > 0 ? (wrapW / trackWidth) * minimapW : minimapW;

  const startMinimapBodyDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest(`.${styles.minimapHandle}`)) return;
      e.preventDefault();
      e.stopPropagation();
      const wrap = scrollWrapRef.current;
      const map = minimapRef.current;
      if (!wrap || !map || trackWidth <= 0 || minimapW <= 0) return;
      const target = e.currentTarget;
      try { target.setPointerCapture(e.pointerId); } catch { /* no-op */ }
      const mapRect = map.getBoundingClientRect();
      const viewportLeftAtStart = (wrap.scrollLeft / trackWidth) * minimapW;
      const grabOffsetPx = e.clientX - mapRect.left - viewportLeftAtStart;
      const viewportWidthPx = (wrap.clientWidth / trackWidth) * minimapW;
      const maxLeftPx = Math.max(0, minimapW - viewportWidthPx);

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const wantLeft = ev.clientX - mapRect.left - grabOffsetPx;
        const clampedLeft = Math.max(0, Math.min(maxLeftPx, wantLeft));
        wrap.scrollLeft = (clampedLeft / minimapW) * trackWidth;
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try { target.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [trackWidth, minimapW, scrollWrapRef],
  );

  const startMinimapHandleDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, edge: 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      const map = minimapRef.current;
      const wrap = scrollWrapRef.current;
      if (!map || !wrap || !totalDur || minimapW <= 0) return;

      const target = e.currentTarget;
      try { target.setPointerCapture(e.pointerId); } catch { /* no-op */ }

      const mapRect = map.getBoundingClientRect();
      const wrapEffW = wrapW;
      const startLeftPx = (scrollLeft / trackWidth) * minimapW;
      const startRightPx = ((scrollLeft + wrapEffW) / trackWidth) * minimapW;
      const minWidthPx = 8;

      handleDragActiveRef.current = true;
      setHandleDragRect({
        left: startLeftPx,
        width: Math.max(minWidthPx, startRightPx - startLeftPx),
      });

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const mouseX = Math.max(0, Math.min(minimapW, ev.clientX - mapRect.left));
        let rawLeft: number;
        let rawRight: number;
        if (edge === 'left') {
          rawLeft = Math.max(0, Math.min(mouseX, startRightPx - minWidthPx));
          rawRight = startRightPx;
        } else {
          rawRight = Math.min(minimapW, Math.max(mouseX, startLeftPx + minWidthPx));
          rawLeft = startLeftPx;
        }
        const visibleFrac = Math.max(0.0001, (rawRight - rawLeft) / minimapW);
        const wantPxPerSec = wrapEffW / (visibleFrac * totalDur);
        // Snap to sticky-fit when the requested zoom is at-or-below fit.
        // Two reasons: (a) it eliminates floating-point drift in the
        // clamp/actualWidth math that left a sub-pixel gap so the user
        // couldn't drag a handle all the way to the edge; (b) storing the
        // -1 sentinel means future wrapW changes (e.g. scrollbar settle,
        // window resize) re-derive fitPxPerSec instead of leaving the
        // timeline frozen at a now-stale numeric value — which is what
        // produced the "second 15 sits too far left, empty space on the
        // right" symptom (timeline narrower than wrap because stored
        // pxPerSec < new fitPxPerSec).
        const atFit = wantPxPerSec <= fitPxPerSec + 0.01;
        const newPxPerSec = atFit
          ? fitPxPerSec
          : Math.min(pxPerSecMax, wantPxPerSec);
        let visualLeft: number;
        let actualWidth: number;
        if (atFit) {
          // At fit there's nothing to scroll and the rect spans the full
          // strip — set both visuals exactly so we never carry FP drift.
          visualLeft = 0;
          actualWidth = minimapW;
        } else {
          const actualVisibleFrac = wrapEffW / (newPxPerSec * totalDur);
          actualWidth = Math.max(
            minWidthPx,
            Math.min(minimapW, actualVisibleFrac * minimapW),
          );
          // Only visualLeft is used below; the right edge follows as
          // visualLeft + actualWidth.
          if (edge === 'left') {
            visualLeft = Math.max(0, startRightPx - actualWidth);
          } else {
            visualLeft = startLeftPx + actualWidth > minimapW
              ? minimapW - actualWidth
              : startLeftPx;
          }
        }
        if (atFit) {
          wrap.scrollLeft = 0;
        } else {
          const newTrackW = Math.max(wrapEffW, newPxPerSec * totalDur);
          const visualRight = visualLeft + actualWidth;
          if (edge === 'left') {
            wrap.scrollLeft = (visualRight / minimapW) * newTrackW - wrapEffW;
          } else {
            wrap.scrollLeft = (visualLeft / minimapW) * newTrackW;
          }
        }
        // -1 sentinel re-engages sticky-fit so the timeline tracks wrapW
        // changes instead of freezing at a snapshot of fitPxPerSec.
        setPxPerSecManual(atFit ? -1 : newPxPerSec);
        setHandleDragRect({ left: visualLeft, width: actualWidth });
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        try { target.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
        setScrollLeft(wrap.scrollLeft);
        handleDragActiveRef.current = false;
        setHandleDragRect(null);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [
      totalDur,
      minimapW,
      wrapW,
      scrollLeft,
      trackWidth,
      fitPxPerSec,
      pxPerSecMax,
      scrollWrapRef,
      setScrollLeft,
      setPxPerSecManual,
      handleDragActiveRef,
    ],
  );

  // Multi-Voice (#173): partition chips into per-voice mini-rows. Each row
  // gets a flex height so all rows together fill the same 16px the single-
  // track minimap occupies — viewport rect + handles span the full height
  // unchanged. Single-voice rendering (no activeVoiceIds OR length < 2)
  // keeps the legacy flat layout bit-for-bit.
  const isMultiVoice = (activeVoiceIds?.length ?? 0) >= 2;
  const chipsByVoice = useMemo(() => {
    if (!isMultiVoice) return null;
    const map = new Map<string, MinimapChip[]>();
    for (const vid of activeVoiceIds!) map.set(vid, []);
    for (const c of chips) {
      const vid = c.voiceId ?? activeVoiceIds![0];
      const bucket = map.get(vid) ?? map.set(vid, []).get(vid)!;
      bucket.push(c);
    }
    return map;
  }, [isMultiVoice, activeVoiceIds, chips]);

  return (
    <div ref={minimapRef} className={styles.minimap}>
      {isMultiVoice && chipsByVoice ? (
        <div className={styles.miniTracks}>
          {activeVoiceIds!.map((vid) => {
            const trackChips = chipsByVoice.get(vid) ?? [];
            const color = voiceColorVar(vid, assetVoiceIds ?? []);
            return (
              <div key={`mm-track-${vid}`} className={styles.miniTrack}>
                {trackChips.map((c, i) => {
                  const left = totalDur > 0 ? (c.offset / totalDur) * minimapW : 0;
                  const width = totalDur > 0
                    ? Math.max(2, (c.duration / totalDur) * minimapW)
                    : 2;
                  return (
                    <div
                      key={`mm-chip-${vid}-${i}`}
                      className={styles.miniChip}
                      style={{
                        left: `${left}px`,
                        width: `${width}px`,
                        background: color,
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        chips.map((c, i) => {
          const left = totalDur > 0 ? (c.offset / totalDur) * minimapW : 0;
          const width = totalDur > 0 ? Math.max(2, (c.duration / totalDur) * minimapW) : 2;
          return (
            <div
              key={`mm-chip-${i}`}
              className={styles.minimapChip}
              style={{ left: `${left}px`, width: `${width}px` }}
            />
          );
        })
      )}
      <div
        className={styles.minimapViewport}
        style={
          handleDragRect
            ? { left: `${handleDragRect.left}px`, width: `${handleDragRect.width}px` }
            : { left: `${minimapViewportLeft}px`, width: `${Math.max(8, minimapViewportWidth)}px` }
        }
        onPointerDown={startMinimapBodyDrag}
      >
        <div
          className={[styles.minimapHandle, styles.minimapHandleLeft].filter(Boolean).join(' ')}
          onPointerDown={(e) => startMinimapHandleDrag(e, 'left')}
        />
        <div
          className={[styles.minimapHandle, styles.minimapHandleRight].filter(Boolean).join(' ')}
          onPointerDown={(e) => startMinimapHandleDrag(e, 'right')}
        />
      </div>
    </div>
  );
}
